import { capitalize, markRaw } from 'vue';

import PurchaseTypes from '@/common/enums/purchase-types.enum';
import {
  IconColoredAdServer,
  IconColoredCadenceBuy,
  IconColoredGuaranteed,
  IconColoredPreferred,
} from '@/common/icons/colored';
import Filter from '@/common/models/query-params/Filter';
import * as FilterComparisonOperator from '@/common/models/query-params/FilterComparisonOperator';
import * as Localizations from '@/i18n/en';
import rootState from '@/store/state';
import isLiveboard from '@/utils/IsLiveboard';
import i18n from '@/vueI18n';

import { FilterLogicalOperator, Option } from '.';
import PURCHASE_TYPES_BY_PRIORITY from './PurchaseTypePriority';
import FilterGroup from './query-params/FilterGroup';

type PurchaseTypesLocalizationKeys = keyof (typeof Localizations)['default']['options']['purchaseTypes'];

export default class PurchaseTypeOptions {
  static getLocalizedOptionName(name: PurchaseTypesLocalizationKeys) {
    return capitalize(i18n.global.t(`options.purchaseTypes.${name}`));
  }

  static options(forDeals = false, forceIncludePMP = false, forFilter = false) {
    const organization = rootState?.orgApp?.organization;
    const organizationSettings = rootState?.orgApp?.organizationSettings;
    const isDemandOrganization = organization?.isDemandOrganization;
    const isSupplyOrganization = organization?.isSupplyOrganization;

    const formattedLabelOption =
      organization?.name === 'LIVE BOARD'
        ? i18n.global.t('messages.liveBoardDSPBuyersOnly')
        : i18n.global.t('messages.hivestackDSPBuyersOnly');

    let nonGuaranteedFormattedLabel: PurchaseTypesLocalizationKeys = 'nonGuaranteedHivestack';
    let nonPreferredFormattedLabel: PurchaseTypesLocalizationKeys = 'preferredHivestack';
    if (isLiveboard()) {
      nonGuaranteedFormattedLabel = isDemandOrganization ? 'adServerNonGuaranteed' : 'nonGuaranteedLiveboard';
      nonPreferredFormattedLabel = isDemandOrganization ? 'preferredPMP' : 'preferredLiveboard';
    }

    const guaranteedLabel = forDeals
      ? PurchaseTypeOptions.getLocalizedOptionName('programmaticGuaranteed')
      : PurchaseTypeOptions.getLocalizedOptionName('adServerGuaranteed');
    const nonGuaranteedLabel = PurchaseTypeOptions.getLocalizedOptionName('nonGuaranteed');
    const preferredLabel = PurchaseTypeOptions.getLocalizedOptionName('preferred');
    const options = [
      Option.fromObject({
        value: PurchaseTypes.GUARANTEED,
        name: guaranteedLabel,
        icon: markRaw(IconColoredGuaranteed),
      }),
      Option.fromObject({
        value: PurchaseTypes.NON_GUARANTEED,
        name: nonGuaranteedLabel,
        icon: markRaw(IconColoredAdServer),
      }),
      Option.fromObject({
        value: PurchaseTypes.PREFERRED,
        name: preferredLabel,
        icon: markRaw(IconColoredPreferred),
      }),
      ...(forDeals
        ? []
        : [
            Option.fromObject({
              value: PurchaseTypes.HOUSE_AD,
              name: PurchaseTypeOptions.getLocalizedOptionName('houseAd'),
              icon: markRaw(IconColoredAdServer),
            }),
          ]),
    ];
    if (isSupplyOrganization && !forDeals && organizationSettings.enableTakeoverPurchaseType) {
      options.push(
        Option.fromObject({
          value: PurchaseTypes.TAKEOVER,
          name: PurchaseTypeOptions.getLocalizedOptionName('takeover'),
        }),
      );
    }

    if (!forDeals && organizationSettings?.enableBonusBuys) {
      options.push(
        Option.fromObject({
          value: PurchaseTypes.BONUS_BUY,
          name: PurchaseTypeOptions.getLocalizedOptionName('bonusBuy'),
        }),
      );
    }

    if (
      ((!forDeals && !forceIncludePMP) || forDeals) &&
      ((isSupplyOrganization && organizationSettings?.uiEnableCadenceBuys) || (forDeals && isDemandOrganization))
    ) {
      options.push(
        Option.fromObject({
          value: PurchaseTypes.CADENCE,
          name: PurchaseTypeOptions.getLocalizedOptionName('cadenceBuy'),
          icon: markRaw(IconColoredCadenceBuy),
        }),
      );
    }

    // DEAL FILTERS
    if (forDeals) {
      options.forEach((option) => {
        switch (option.value) {
          case PurchaseTypes.NON_GUARANTEED:
            option.filters = [new Filter('purchase_type', [PurchaseTypes.NON_GUARANTEED], FilterComparisonOperator.IN)];
            break;
          case PurchaseTypes.PREFERRED:
            option.filters = [new Filter('purchase_type', [PurchaseTypes.PREFERRED], FilterComparisonOperator.IN)];
            break;
          default:
        }
      });
    }

    return options;
  }

  static planOptions(forDeals = false) {
    return PurchaseTypeOptions.options(forDeals).filter(
      (purchaseType) =>
        [PurchaseTypes.TAKEOVER, PurchaseTypes.CADENCE].includes(purchaseType.value as PurchaseTypes) === false,
    );
  }

  static demandOrganizationOptions() {
    const organizationSettings = rootState?.orgApp?.organizationSettings;
    if (organizationSettings?.uiDSPAvailablePurchaseTypes) {
      return organizationSettings.uiDSPAvailablePurchaseTypes.map((x) =>
        PurchaseTypeOptions.options().find((y) => y.value === x),
      );
    }
    return PurchaseTypeOptions.options().filter((purchaseType) =>
      [PurchaseTypes.GUARANTEED, PurchaseTypes.NON_GUARANTEED].includes(purchaseType.value as PurchaseTypes),
    );
  }

  static sidekickDealFilters() {
    const cadenceFilter = new Filter('purchase_type', [PurchaseTypes.CADENCE], FilterComparisonOperator.EQUAL);
    const pgFilter = new Filter('purchase_type', [PurchaseTypes.GUARANTEED], FilterComparisonOperator.EQUAL);
    const ngOtherFilter = new FilterGroup(
      [
        new Filter('purchase_type', [PurchaseTypes.NON_GUARANTEED], FilterComparisonOperator.EQUAL),
        new Filter('for_bidding', [true], FilterComparisonOperator.EQUAL),
      ],
      FilterLogicalOperator.AND,
    ); // Side kick orgs should only see NG Other DSP
    const preferredOtherFilter = new FilterGroup(
      [
        new Filter('purchase_type', [PurchaseTypes.PREFERRED], FilterComparisonOperator.EQUAL),
        new Filter('for_bidding', [true], FilterComparisonOperator.EQUAL),
      ],
      FilterLogicalOperator.AND,
    );

    return {
      cadenceFilter,
      pgFilter,
      ngOtherFilter,
      preferredOtherFilter,
    };
  }

  static sidekickOptions() {
    const pg = Option.fromObject({
      id: PurchaseTypes.GUARANTEED,
      value: PurchaseTypes.GUARANTEED,
      name: PurchaseTypeOptions.getLocalizedOptionName('guaranteed'),
      filters: [this.sidekickDealFilters().pgFilter],
    });
    const ngOtherDsp = Option.fromObject({
      id: PurchaseTypes.NON_GUARANTEED,
      value: PurchaseTypes.NON_GUARANTEED,
      name: PurchaseTypeOptions.getLocalizedOptionName('nonGuaranteed'),
      filters: [this.sidekickDealFilters().ngOtherFilter],
    });
    const preferredOtherDsp = Option.fromObject({
      id: PurchaseTypes.PREFERRED,
      value: PurchaseTypes.PREFERRED,
      name: PurchaseTypeOptions.getLocalizedOptionName('preferred'),
      filters: [this.sidekickDealFilters().preferredOtherFilter],
    });
    const cadence = Option.fromObject({
      id: PurchaseTypes.CADENCE,
      value: PurchaseTypes.CADENCE,
      name: PurchaseTypeOptions.getLocalizedOptionName('cadenceBuy'),
      filters: [this.sidekickDealFilters().cadenceFilter],
    });

    return [pg, ngOtherDsp, preferredOtherDsp, cadence];
  }

  static sortOptionsByPriority(options: Option[]): Option[] {
    return options.sort((a, b) => {
      const aPriority = PURCHASE_TYPES_BY_PRIORITY.indexOf(a.value as PurchaseTypes);
      const bPriority = PURCHASE_TYPES_BY_PRIORITY.indexOf(b.value as PurchaseTypes);
      return aPriority - bPriority;
    });
  }
}
